import {IconButton, Switch} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {HTMLAttributes} from 'react';

import {
  PhotoGuideSlot,
  SALE_VEHICLE_PHOTO_TYPES_BY_GROUP,
  SaleVehiclePhotoType,
  SaleVehiclePhotoTypeGroupSchema,
} from '@dms/api/settings';
import {DragHandle} from '@dms/features/dnd';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {getSaleVehiclePhotoTypeGroupLabel} from '../utils/getSaleVehiclePhotoTypeGroupLabel';
import {getSaleVehiclePhotoTypeLabel} from '../utils/getSaleVehiclePhotoTypeLabel';

const getPhotoTypeGroup = (photoType: SaleVehiclePhotoType) =>
  SaleVehiclePhotoTypeGroupSchema.options.find((group) =>
    SALE_VEHICLE_PHOTO_TYPES_BY_GROUP[group].some((type) => type === photoType)
  );

interface PhotoGuideSlotRowProps extends RequiredTestIdProps {
  slot: PhotoGuideSlot;
  position: number;
  handleProps?: HTMLAttributes<unknown>;
  isReadOnly: boolean;
  isDisabled: boolean;
  onRequiredChange: (required: boolean) => void;
  onRemove: () => void;
}

export function PhotoGuideSlotRow(props: PhotoGuideSlotRowProps) {
  const photoTypeGroup = getPhotoTypeGroup(props.slot.photoType);

  return (
    <Box
      padding={2}
      borderRadius="small"
      border="1px solid"
      borderColor="general.separator"
      backgroundColor="general.white"
      data-testid={props['data-testid']}
    >
      <HStack spacing={3} align="center">
        <Show when={!props.isReadOnly}>
          <DragHandle
            {...props.handleProps}
            aria-label={i18n.t('general.actions.reorder')}
            data-testid={suffixTestId('handle', props)}
          >
            <Icon value="action/drag_indicator" size={4} color="text.tertiary" />
          </DragHandle>
        </Show>
        <Text size="small" color="secondary">
          {props.position}.
        </Text>
        <Box flex={1}>
          <VStack>
            <Text size="small" data-testid={suffixTestId('photoType', props)}>
              {getSaleVehiclePhotoTypeLabel(props.slot.photoType)}
            </Text>
            <Show when={photoTypeGroup}>
              {(group) => (
                <Text size="xSmall" color="tertiary">
                  {getSaleVehiclePhotoTypeGroupLabel(group)}
                </Text>
              )}
            </Show>
          </VStack>
        </Box>
        <Switch
          label={i18n.t('general.labels.required')}
          value={props.slot.required}
          isDisabled={props.isDisabled}
          onChange={props.onRequiredChange}
          data-testid={suffixTestId('required', props)}
        />
        <IconButton
          icon="action/delete"
          size="small"
          severity="danger"
          isDisabled={props.isDisabled}
          aria-label={i18n.t('general.actions.remove')}
          onClick={props.onRemove}
          data-testid={suffixTestId('remove', props)}
        />
      </HStack>
    </Box>
  );
}
